import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { apiGet } from '../lib/api.js';
import { getSocket } from '../lib/socket.js';

dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 48; // px per hour
const VIEWS = ['month', 'week', 'day', 'agenda'];

function hourLabel(h) {
  if (h === 0) return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

function getWindow(view, cursor) {
  switch (view) {
    case 'month': {
      const ms = cursor.startOf('month');
      const gs = ms.startOf('week');
      return { start: gs, end: gs.add(42, 'day').subtract(1, 'second') };
    }
    case 'week':
      return { start: cursor.startOf('week'), end: cursor.endOf('week') };
    case 'day':
      return { start: cursor.startOf('day'), end: cursor.endOf('day') };
    case 'agenda':
      return { start: cursor.startOf('day'), end: cursor.add(30, 'day').endOf('day') };
    default:
      return { start: cursor.startOf('month'), end: cursor.endOf('month') };
  }
}

function navTitle(view, cursor) {
  switch (view) {
    case 'month':
      return cursor.format('MMMM YYYY');
    case 'week': {
      const s = cursor.startOf('week');
      const e = cursor.endOf('week');
      if (s.month() === e.month()) return `${s.format('MMM D')} – ${e.format('D, YYYY')}`;
      if (s.year() === e.year()) return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`;
      return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
    }
    case 'day':
      return cursor.format('dddd, MMM D');
    case 'agenda': {
      const s = cursor.startOf('day');
      return `${s.format('MM/DD/YYYY')} – ${s.add(30, 'day').format('MM/DD/YYYY')}`;
    }
    default:
      return cursor.format('MMMM YYYY');
  }
}

function stepCursor(view, cursor, dir) {
  switch (view) {
    case 'month': return cursor.add(dir, 'month');
    case 'week': return cursor.add(dir * 7, 'day');
    case 'day': return cursor.add(dir, 'day');
    case 'agenda': return cursor.add(dir * 30, 'day');
    default: return cursor.add(dir, 'month');
  }
}

// Normalize event to { id, title, start (dayjs), end (dayjs), isAllDay, isGoogle, htmlLink, raw }
function normalizeAppt(a) {
  const start = dayjs(a.startAt);
  const end = a.endAt ? dayjs(a.endAt) : start.add(30, 'minute');
  return { id: a._id, title: a.title || 'Appointment', start, end, isAllDay: false, isGoogle: false, htmlLink: null, raw: a };
}

function normalizeGEvent(ev) {
  const isAllDay = !!ev.start?.date && !ev.start?.dateTime;
  const start = isAllDay ? dayjs(ev.start.date) : dayjs(ev.start.dateTime);
  const end = isAllDay
    ? (ev.end?.date ? dayjs(ev.end.date) : start.add(1, 'day'))
    : (ev.end?.dateTime ? dayjs(ev.end.dateTime) : start.add(1, 'hour'));
  return { id: ev.id, title: ev.summary || '(No title)', start, end, isAllDay, isGoogle: true, htmlLink: ev.htmlLink || null, raw: ev };
}

/* ─── Event chip colours ─── */
function chipCls(ev, mode = 'block') {
  if (mode === 'block') {
    return ev.isGoogle
      ? 'bg-blue-500/10 border-l-2 border-blue-500/70 text-blue-300'
      : 'bg-civix-cyan/10 border-l-2 border-civix-cyan text-civix-cyan';
  }
  // inline dot / agenda row border
  return ev.isGoogle ? 'border-l-2 border-blue-500/70' : 'border-l-2 border-civix-cyan';
}

/* ─── Month View ─── */
function MonthView({ cursor, allEvents }) {
  const today = dayjs();
  const { gridDays } = useMemo(() => {
    const ms = cursor.startOf('month');
    const gs = ms.startOf('week');
    return { gridDays: Array.from({ length: 42 }, (_, i) => gs.add(i, 'day')) };
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = new Map();
    allEvents.forEach((ev) => {
      const key = ev.start.format('YYYY-MM-DD');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });
    return map;
  }, [allEvents]);

  return (
    <div className="flex-1 flex flex-col gap-1 min-h-0">
      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((d) => (
          <div key={d} className="text-[10px] font-semibold uppercase tracking-wider text-white/40 px-2 py-1">
            {d}
          </div>
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-7 gap-1 flex-1">
        {gridDays.map((d, i) => {
          const inMonth = d.month() === cursor.month();
          const isToday = d.isSame(today, 'day');
          const key = d.format('YYYY-MM-DD');
          const evs = byDay.get(key) || [];
          const visible = evs.slice(0, 3);
          const overflow = evs.length - visible.length;

          return (
            <div
              key={i}
              className={`panel p-2 flex flex-col transition-opacity ${inMonth ? '' : 'opacity-40'} ${isToday ? 'border-civix-purple/60 ring-1 ring-civix-purple/30' : ''}`}
              style={{ minHeight: 110 }}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className={`font-display text-sm font-semibold ${isToday ? 'text-civix-purple' : 'text-white/70'}`}>
                  {d.format('D')}
                </span>
                {overflow > 0 && <span className="text-[9px] text-white/40">+{overflow} more</span>}
              </div>
              <div className="space-y-0.5 overflow-hidden flex-1">
                {visible.map((ev) => (
                  ev.isGoogle ? (
                    <a
                      key={ev.id}
                      href={ev.htmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`${ev.title}${!ev.isAllDay ? ` — ${ev.start.format('h:mm A')}` : ''}`}
                      className="block px-1.5 py-0.5 text-[10px] rounded-sm bg-blue-500/10 border-l-2 border-blue-500/60 hover:bg-blue-500/20 transition"
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-[8px] font-bold text-blue-400">G</span>
                        <span className="text-blue-300/80 truncate">
                          {ev.isAllDay ? 'All day' : ev.start.format('h:mma')}
                        </span>
                      </div>
                      <div className="text-white/80 truncate">{ev.title}</div>
                    </a>
                  ) : (
                    <div
                      key={ev.id}
                      title={`${ev.start.format('h:mm A')} — ${ev.title}`}
                      className="px-1.5 py-0.5 text-[10px] rounded-sm bg-civix-cyan/15 border-l-2 border-civix-cyan"
                    >
                      <div className="text-civix-cyan font-medium truncate">
                        {ev.start.format('h:mma')} {ev.title}
                      </div>
                      {ev.raw?.lead?.firstName && (
                        <div className="text-white/50 truncate">{ev.raw.lead.firstName} {ev.raw.lead.lastName || ''}</div>
                      )}
                    </div>
                  )
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Hour Grid (shared by Week and Day) ─── */
function HourGrid({ days, allEvents, showAllDay = true }) {
  const today = dayjs();
  const [nowPct, setNowPct] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    function update() {
      const now = dayjs();
      setNowPct((now.hour() * 60 + now.minute()) / (24 * 60));
    }
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, []);

  // Scroll to 7am on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 7 * HOUR_HEIGHT - 20;
    }
  }, []);

  // Partition events by day column
  const eventsByDay = useMemo(() => {
    const map = new Map();
    days.forEach((d) => map.set(d.format('YYYY-MM-DD'), { timed: [], allDay: [] }));
    allEvents.forEach((ev) => {
      const key = ev.start.format('YYYY-MM-DD');
      if (map.has(key)) {
        if (ev.isAllDay) map.get(key).allDay.push(ev);
        else map.get(key).timed.push(ev);
      }
    });
    return map;
  }, [days, allEvents]);

  const hasAnyAllDay = useMemo(() =>
    days.some((d) => (eventsByDay.get(d.format('YYYY-MM-DD'))?.allDay || []).length > 0),
    [days, eventsByDay]
  );

  return (
    <div className="flex flex-col flex-1 min-h-0 panel overflow-hidden">
      {/* Day headers */}
      <div className="flex border-b border-civix-border/20">
        <div className="w-14 shrink-0" />
        {days.map((d) => {
          const isToday = d.isSame(today, 'day');
          return (
            <div
              key={d.format('YYYY-MM-DD')}
              className={`flex-1 text-center py-2 ${isToday ? 'bg-civix-purple/10' : ''}`}
            >
              <div className="text-[10px] text-white/40 uppercase tracking-wider">{d.format('ddd')}</div>
              <div className={`font-display text-lg font-semibold ${isToday ? 'text-civix-purple' : 'text-white/80'}`}>
                {d.format('D')}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day row */}
      {showAllDay && hasAnyAllDay && (
        <div className="flex border-b border-civix-border/20 min-h-[28px]">
          <div className="w-14 shrink-0 text-[9px] text-white/30 pr-2 pt-1 text-right">all day</div>
          {days.map((d) => {
            const key = d.format('YYYY-MM-DD');
            const allDayEvs = eventsByDay.get(key)?.allDay || [];
            const isToday = d.isSame(today, 'day');
            return (
              <div key={key} className={`flex-1 px-1 py-1 ${isToday ? 'bg-civix-purple/5' : ''}`}>
                {allDayEvs.map((ev) => (
                  ev.isGoogle ? (
                    <a
                      key={ev.id}
                      href={ev.htmlLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={ev.title}
                      className="block text-[10px] px-1 py-0.5 rounded-sm bg-blue-500/20 text-blue-300 truncate mb-0.5 hover:bg-blue-500/30 transition"
                    >
                      {ev.title}
                    </a>
                  ) : (
                    <div key={ev.id} title={ev.title}
                      className="text-[10px] px-1 py-0.5 rounded-sm bg-civix-cyan/20 text-civix-cyan truncate mb-0.5"
                    >
                      {ev.title}
                    </div>
                  )
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Scrollable hour grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto relative">
        <div className="flex" style={{ height: HOUR_HEIGHT * 24 }}>
          {/* Hour labels */}
          <div className="w-14 shrink-0 relative">
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute right-2 text-[10px] text-white/30 select-none"
                style={{ top: h * HOUR_HEIGHT - 8 }}
              >
                {hourLabel(h)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((d) => {
            const key = d.format('YYYY-MM-DD');
            const timedEvs = eventsByDay.get(key)?.timed || [];
            const isToday = d.isSame(today, 'day');

            return (
              <div
                key={key}
                className={`flex-1 relative ${isToday ? 'bg-civix-purple/4' : ''}`}
                style={{ boxShadow: 'inset 1px 0 0 0 rgba(255,255,255,0.04)' }}
              >
                {/* Hour lines (very subtle) */}
                {HOURS.map((h) => (
                  h === 0 ? null : (
                    <div
                      key={h}
                      className="absolute left-0 right-0 pointer-events-none"
                      style={{ top: h * HOUR_HEIGHT, height: 1, background: 'rgba(255,255,255,0.04)' }}
                    />
                  )
                ))}

                {/* Current time line */}
                {isToday && nowPct !== null && (
                  <div
                    className="absolute left-0 right-0 z-10 pointer-events-none"
                    style={{ top: nowPct * HOUR_HEIGHT * 24 }}
                  >
                    <div className="h-px bg-red-400/80 relative">
                      <div className="absolute -left-1 -top-1.5 w-2.5 h-2.5 rounded-full bg-red-400" />
                    </div>
                  </div>
                )}

                {/* Timed events */}
                {timedEvs.map((ev) => {
                  const startMins = ev.start.hour() * 60 + ev.start.minute();
                  const endMins = ev.end.hour() * 60 + ev.end.minute();
                  const durationMins = Math.max(endMins - startMins, 15);
                  const top = startMins * (HOUR_HEIGHT / 60);
                  const height = Math.max(durationMins * (HOUR_HEIGHT / 60), 16);
                  const isShort = height < 30;

                  return (
                    ev.isGoogle ? (
                      <a
                        key={ev.id}
                        href={ev.htmlLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={ev.title}
                        className="absolute left-1 right-1 rounded-sm overflow-hidden border-l-2 border-blue-500 bg-blue-500/20 hover:bg-blue-500/30 transition px-1.5 py-0.5 z-5"
                        style={{ top, height }}
                      >
                        <div className={`text-blue-300 font-medium truncate ${isShort ? 'text-[9px]' : 'text-[10px]'}`}>{ev.title}</div>
                        {!isShort && (
                          <div className="text-[9px] text-blue-300/60">{ev.start.format('h:mm')}–{ev.end.format('h:mma')}</div>
                        )}
                      </a>
                    ) : (
                      <div
                        key={ev.id}
                        title={ev.title}
                        className="absolute left-1 right-1 rounded-sm overflow-hidden border-l-2 border-civix-cyan bg-civix-cyan/20 px-1.5 py-0.5 z-5"
                        style={{ top, height }}
                      >
                        <div className={`text-civix-cyan font-medium truncate ${isShort ? 'text-[9px]' : 'text-[10px]'}`}>{ev.title}</div>
                        {!isShort && (
                          <div className="text-[9px] text-civix-cyan/60">{ev.start.format('h:mm')}–{ev.end.format('h:mma')}</div>
                        )}
                      </div>
                    )
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Week View ─── */
function WeekView({ cursor, allEvents }) {
  const days = useMemo(() => {
    const ws = cursor.startOf('week');
    return Array.from({ length: 7 }, (_, i) => ws.add(i, 'day'));
  }, [cursor]);

  return <HourGrid days={days} allEvents={allEvents} showAllDay />;
}

/* ─── Day View ─── */
function DayView({ cursor, allEvents }) {
  const days = useMemo(() => [cursor.startOf('day')], [cursor]);
  return <HourGrid days={days} allEvents={allEvents} showAllDay />;
}

/* ─── Agenda View ─── */
function AgendaView({ cursor, allEvents }) {
  const today = dayjs();
  const windowEnd = cursor.add(30, 'day').endOf('day');

  const rows = useMemo(() => {
    // Filter to window
    const evs = allEvents
      .filter((ev) => ev.start.isSameOrAfter(cursor.startOf('day')) && ev.start.isSameOrBefore(windowEnd))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    // Group by date
    const grouped = [];
    let lastDate = null;
    evs.forEach((ev) => {
      const dateKey = ev.start.format('YYYY-MM-DD');
      const showDate = dateKey !== lastDate;
      lastDate = dateKey;
      grouped.push({ ev, showDate, dateKey });
    });
    return grouped;
  }, [allEvents, cursor]);

  if (rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-white/30 text-sm">
        No events in the next 30 days
      </div>
    );
  }

  return (
    <div className="flex-1 panel overflow-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-civix-border/30">
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/40 w-36">Date</th>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/40 w-44">Time</th>
            <th className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-white/40">Event</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ ev, showDate, dateKey }, idx) => {
            const isToday = dayjs(dateKey).isSame(today, 'day');
            const rowBg = idx % 2 === 0 ? '' : 'bg-white/2';
            const borderCls = ev.isGoogle ? 'border-l-2 border-blue-500/60' : 'border-l-2 border-civix-cyan';

            return (
              <tr
                key={`${ev.id}-${idx}`}
                className={`${rowBg} ${borderCls} hover:bg-white/5 transition cursor-pointer`}
                onClick={() => ev.isGoogle && ev.htmlLink && window.open(ev.htmlLink, '_blank')}
              >
                <td className="px-4 py-2 align-top">
                  {showDate && (
                    <div className={`font-semibold ${isToday ? 'text-civix-purple' : 'text-white/70'}`}>
                      {dayjs(dateKey).format('ddd MMM D')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-white/50 align-top whitespace-nowrap">
                  {ev.isAllDay
                    ? 'All day'
                    : `${ev.start.format('h:mm a')} – ${ev.end.format('h:mm a')}`}
                </td>
                <td className="px-4 py-2 align-top">
                  <div className="flex items-center gap-2">
                    {ev.isGoogle && (
                      <span className="text-[9px] font-bold text-blue-400 bg-blue-500/20 px-1 rounded-sm">G</span>
                    )}
                    <span className={ev.isGoogle ? 'text-blue-300' : 'text-civix-cyan'} title={ev.title}>
                      {ev.title}
                    </span>
                    {ev.isGoogle && ev.htmlLink && (
                      <ExternalLink className="w-3 h-3 text-white/30 shrink-0" />
                    )}
                  </div>
                  {!ev.isGoogle && ev.raw?.lead?.firstName && (
                    <div className="text-white/40 text-xs mt-0.5">
                      {ev.raw.lead.firstName} {ev.raw.lead.lastName || ''}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Main Calendar Page ─── */
export default function CalendarPage() {
  const [cursor, setCursor] = useState(dayjs());
  const [view, setView] = useState('month');
  const today = dayjs();
  const qc = useQueryClient();

  // A prospect just booked on the public page → refresh the grid live.
  useEffect(() => {
    const socket = getSocket();
    if (!socket?.on) return undefined;
    const refresh = () => qc.invalidateQueries({ queryKey: ['cal-appts'] });
    socket.on('appointment:new', refresh);
    return () => socket.off('appointment:new', refresh);
  }, [qc]);

  // Connection status
  const { data: connData } = useQuery({
    queryKey: ['gcal-connection'],
    queryFn: () => apiGet('/calendar/connection'),
    refetchOnWindowFocus: true,
  });
  const gcConnected = !!connData?.connected;

  // Compute fetch window
  const { start: winStart, end: winEnd } = useMemo(() => getWindow(view, cursor), [view, cursor]);
  const rangeKey = `${view}_${winStart.toISOString()}_${winEnd.toISOString()}`;

  // Fetch CIVIX appointments
  const { data: apptsData, isLoading: apptsLoading } = useQuery({
    queryKey: ['cal-appts', rangeKey],
    queryFn: () =>
      apiGet(`/calendar?start=${winStart.toISOString()}&end=${winEnd.toISOString()}`),
  });

  // Fetch Google events
  const maxResults = view === 'month' ? '&maxResults=500' : '';
  const { data: gData, isLoading: gLoading } = useQuery({
    queryKey: ['gcal-events', rangeKey],
    queryFn: () =>
      apiGet(`/calendar/google-events?timeMin=${winStart.toISOString()}&timeMax=${winEnd.toISOString()}${maxResults}`),
    enabled: gcConnected,
  });

  const isLoading = apptsLoading || (gcConnected && gLoading);

  // Surface a real error state when Google refuses. The /google-events
  // endpoint returns { ok:true, events:[], error } on failure so old
  // clients don't break; we key off `error` to show a reconnect banner
  // instead of the green 'Google Connected' badge (which was lying about
  // a dead refresh token — audit 2026-08-26).
  const gcalError = gData?.error || null;
  const needsReconnect = !!gcalError;

  // Normalise all events, then dedupe. Both a CIVIX appointment AND its
  // Google-Calendar mirror will show up when GCal sync is on — same title,
  // same start-minute. Prefer the CIVIX event (has richer metadata like
  // lead links, disposition, etc.) and drop the Google duplicate. Audit
  // finding 2026-08-25: month view showed "2:00pm Presentation — Timothy
  // Isham" twice in the same day cell.
  const allEvents = useMemo(() => {
    const appts = (apptsData?.appointments || []).map(normalizeAppt);
    const gevts = (gData?.events || []).map(normalizeGEvent);
    const dedupeKey = (ev) => `${(ev.title || '').trim().toLowerCase()}|${ev.start?.toISOString?.() || ''}`;
    const seen = new Set(appts.map(dedupeKey));
    const gevtsUnique = gevts.filter((ev) => !seen.has(dedupeKey(ev)));
    return [...appts, ...gevtsUnique];
  }, [apptsData, gData]);

  function goToday() { setCursor(dayjs()); }
  function prev() { setCursor((c) => stepCursor(view, c, -1)); }
  function next() { setCursor((c) => stepCursor(view, c, 1)); }

  // Animate view transitions
  const [animKey, setAnimKey] = useState(0);
  function switchView(v) {
    setView(v);
    setAnimKey((k) => k + 1);
  }

  return (
    <div className="flex flex-col gap-4 max-w-[1500px] mx-auto h-full" style={{ minHeight: 0 }}>
      {/* ── Header ── */}
      <header className="flex items-start justify-between gap-4 flex-wrap shrink-0">
        {/* Left: title + status */}
        <div className="min-w-0">
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Schedule</div>
          <h1 className="font-display text-3xl font-bold mt-1">Calendar</h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {isLoading ? (
              <span className="text-sm text-white/40 italic">Loading…</span>
            ) : (
              <span className="text-sm text-white/50">
                {gcConnected
                  ? `Synced with Google Calendar · ${connData?.email || ''}`
                  : 'Connect Google Calendar in Settings to sync events'}
              </span>
            )}
            {gcConnected && !isLoading && !needsReconnect && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                Google Connected
              </span>
            )}
            {needsReconnect && (
              <a
                href="/settings?tab=integrations"
                className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 hover:bg-yellow-500/30 transition"
                title={gcalError || ''}
              >
                Reconnect Google Calendar
              </a>
            )}
          </div>
        </div>

        {/* Center: nav controls */}
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <button
            onClick={goToday}
            className="px-3 py-1.5 text-xs rounded-lg border border-civix-border/60 hover:bg-civix-panelLight text-white/70 hover:text-white transition"
          >
            Today
          </button>
          <button
            onClick={prev}
            className="p-1.5 rounded-lg border border-civix-border/60 hover:bg-civix-panelLight text-white/70 hover:text-white transition"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-display text-base font-semibold min-w-[180px] text-center">
            {navTitle(view, cursor)}
          </div>
          <button
            onClick={next}
            className="p-1.5 rounded-lg border border-civix-border/60 hover:bg-civix-panelLight text-white/70 hover:text-white transition"
            aria-label="Next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Right: view toggle pill */}
        <div className="flex items-center rounded-lg overflow-hidden border border-civix-border/40 bg-civix-panelLight/40">
          {VIEWS.map((v) => {
            const active = v === view;
            return (
              <button
                key={v}
                onClick={() => switchView(v)}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition ${
                  active
                    ? 'bg-civix-purple/20 text-white border-x border-civix-purple/40'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {v === 'agenda' ? 'Agenda' : v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            );
          })}
        </div>
      </header>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-white/50 shrink-0">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-xs bg-civix-cyan inline-block" /> CIVIX appointment
        </span>
        {gcConnected && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-xs bg-blue-500/70 inline-block" /> Google event
          </span>
        )}
      </div>

      {/* ── View body ── */}
      <div
        key={animKey}
        className="flex-1 min-h-0 animate-fadeIn"
        style={{ animation: 'fadeIn 0.18s ease' }}
      >
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
        {view === 'month' && <MonthView cursor={cursor} allEvents={allEvents} />}
        {view === 'week' && <WeekView cursor={cursor} allEvents={allEvents} />}
        {view === 'day' && <DayView cursor={cursor} allEvents={allEvents} />}
        {view === 'agenda' && <AgendaView cursor={cursor} allEvents={allEvents} />}
      </div>

      {/* Connect CTA */}
      {!gcConnected && (
        <div className="panel p-4 flex items-center gap-3 text-sm shrink-0">
          <ExternalLink className="w-4 h-4 text-civix-cyan" />
          <span className="text-white/70">Want your Google Calendar events to show here? Connect it in</span>
          <a href="/settings" className="text-civix-cyan hover:underline">Settings → Google Calendar</a>
        </div>
      )}
    </div>
  );
}
